import { err, ok, Result } from 'neverthrow';

import { NetworkError } from '@rover/rsdk/src/errors';
import { NativePickerAsset } from '@rover/types/src/ImagePicker';

import { HttpResult, post } from '../../Network';
import MediaFile from '../MediaFile';
import { MediaType, UploadImageOptions } from '../types';

import MediaUploadError, {
  MediaUploadClientError,
  MediaUploadNetworkError,
  MediaUploadServerError,
  MediaUploadUnknownError,
} from './MediaUploadError';
import { AttachableUploadTask } from './UploadTask';

export type MediaUploadAuthResponseCamel = { uploadUrl: string };
export type MediaUploadAuthResponseSnake = { upload_url: string };

export const mediaUploadAuthCreate = (
  { file, type }: MediaFile,
  // UploadImageOptions is optional here because videos do not require them
  opts?: Partial<UploadImageOptions>
): Promise<HttpResult<MediaUploadAuthResponseSnake>> => {
  const url = `/api/v7/${type === MediaType.IMAGE ? 'images' : 'videos'}/upload/authorize/`;

  let config: { fileName: string; contentType: string };
  // refine to File (web) or NativePickerAsset (RxN)
  if (File && file instanceof File) {
    config = { fileName: file.name, contentType: file.type };
  } else {
    config = {
      fileName: (file as NativePickerAsset).fileName || '',
      contentType: (file as NativePickerAsset).type || '',
    };
  }
  return post<MediaUploadAuthResponseSnake>(url, {
    ...config,
    ...opts,
  });
};

export function getResultOfUploadHttpAction(
  response: HttpResult<unknown>,
  action: string
): Result<true, MediaUploadError> {
  if (response.isOk()) {
    return ok(true);
  }
  if (response.error instanceof NetworkError) {
    return err(new MediaUploadNetworkError());
  }
  if (response.error.statusCode.toString().startsWith('4')) {
    return err(new MediaUploadClientError());
  }
  if (response.error.statusCode.toString().startsWith('5')) {
    return err(new MediaUploadServerError());
  }
  console.error(response); // eslint-disable-line no-console
  return err(new MediaUploadUnknownError(new Error(`Unknown error during ${action}`)));
}

export async function attach(task: AttachableUploadTask): Promise<Result<true, MediaUploadError>> {
  const { type: mediaType } = task.file;
  const { url: mediaUrl } = task.result.value;
  const { caption, contentObjectMediaUrl } = task.options.attach;

  let body;
  if (mediaType === MediaType.IMAGE) {
    body = {
      imageUrl: mediaUrl,
      ...(caption !== undefined && { caption }),
    };
  } else {
    body = {
      videoUrl: mediaUrl,
    };
  }
  const res = await post<unknown>(contentObjectMediaUrl, body);
  return getResultOfUploadHttpAction(res, `${mediaType} attach`);
}
